<template>
  <v-card flat max-width="1280" class="pl-md-6">
    <guide-tour config-key="message.closeNotification">
      <div v-if="showSlotClosureLink">
        要關閉線上預約空檔，請前往<router-link
          :to="{name: 'freeTimeSetting', query: {section: 'slot-closure'}}"
          class="link_blue--text"
        >休診日關閉空檔</router-link>
      </div>
    </guide-tour>

    <div v-if="!pageLoading" class="px-4 mt-4 pb-6 pb-md-8">
      <v-card outlined rounded :loading="loading">
        <div class="grey-8 px-md-8 py-md-4">
          <v-toolbar flat color="grey-8">
            <v-btn outlined class="mr-4" color="grey darken-2" @click="setToday">
              今日
            </v-btn>
            <v-btn fab text small color="grey darken-2" @click="prev">
              <v-icon small>
                mdi-chevron-left
              </v-icon>
            </v-btn>
            <v-btn fab text small color="grey darken-2" @click="next">
              <v-icon small>
                mdi-chevron-right
              </v-icon>
            </v-btn>
            <v-toolbar-title v-if="$refs.calendar">
              {{ $refs.calendar.title }}
            </v-toolbar-title>
            <v-spacer/>
          </v-toolbar>
          <div class="calendar-instruction red-6--text text-body-2 font-weight-medium text-right px-4 pb-2">
            *{{ calendarInstruction }}
          </div>
          <v-sheet height="calc(100vh - 350px)">
            <v-calendar
              ref="calendar"
              v-model="focus"
              class="close-notification-calendar"
              :class="isEdit ? 'calendar--editable' : 'calendar--preview'"
              :locale="$i18n.locale"
              :aria-readonly="!isEdit"
              color="primary"
              @click:day="toggleClosed"
              @click:date="toggleClosed"
            >
              <template v-slot:day="{ past, date }">
                <div
                  class="calendar-day-status d-flex align-center justify-center"
                >
                  <template v-if="closedDates.includes(date)">
                    <div v-if="isEdit">
                      <v-icon color="red-6" size="32">mdi-close-circle</v-icon>
                    </div>
                    <v-chip v-else small color="red-6 lighten-4 red-6--text">
                      <v-icon small>mdi-close-circle</v-icon>
                      <span class="ml-1">已關閉約診提醒</span>
                    </v-chip>
                  </template>
                  <v-icon
                    v-else-if="isEdit && !past"
                    class="calendar-day-hover-icon"
                    color="grey-4"
                    size="32"
                  >
                    mdi-close-circle
                  </v-icon>
                </div>
              </template>
            </v-calendar>
          </v-sheet>
        </div>
        <v-card-actions
          class="close-notification-actions py-4"
          :class="{'close-notification-actions--editing': isEdit}"
        >
          <v-spacer/>
          <template v-if="isEdit">
            <span
              v-if="hasUnsavedChanges"
              aria-live="polite"
              class="unsaved-message red-6--text font-weight-medium mr-3"
            >
              尚未儲存
            </span>
            <v-btn dark color="grey-7" width="100" @click="onCancel">取消</v-btn>
            <v-btn dark color="red-6" width="100" @click="onConfirm">儲存</v-btn>
          </template>
          <v-btn v-else color="primary" width="100" @click="onEdit">
            <v-icon small>mdi-pencil</v-icon>
            編輯
          </v-btn>
        </v-card-actions>
      </v-card>
      <confirm-dialog
        v-if="toggleConfirmDialog"
        v-model="toggleConfirmDialog"
        :title="confirmTitle"
        :content="confirmContent"
        :confirm-btn-text="confirmText"
        :cancel-btn-text="cancelText"
        :confirm-btn-color="confirmColor"
        :cancel-btn-color="cancelColor"
        @confirm="confirmFunc"
      />
    </div>
  </v-card>
</template>

<script>
import GuideTour from "@/components/utils/GuideTour.vue";
import { mapGetters } from "vuex";
import { getRemindSettingCloseAPI, setRemindSettingCloseAPI } from "@/api";
import ConfirmDialog from "@/components/utils/ConfirmDialog.vue";

export default {
  name: 'CloseNotificationSetting',

  components: {ConfirmDialog, GuideTour},

  data() {
    return {
      pageLoading: false,
      loading: false,
      focus: '',
      closedDates: [],
      ocClosedDates: [],
      isEdit: false,
      toggleConfirmDialog: false,
      confirmTitle: '',
      confirmContent: '',
      confirmText: '',
      cancelText: '',
      confirmColor: null,
      cancelColor: null,
      confirmFunc: null,
      cancelFunc: null
    }
  },

  computed: {
    ...mapGetters([
      'axiosParamData',
      'canAccess',
      'clinicUser',
      'isSuper'
    ]),
    // 休診日區塊只有診所帳號看得到，子帳號沒有線上預約設定權限時點了也進不去
    showSlotClosureLink() {
      return this.clinicUser && (this.isSuper || this.canAccess('freeTime'))
    },
    calendarInstruction() {
      return this.isEdit
        ? '請點選日期格關閉當日約診提醒，並點擊下方儲存設定'
        : '請點擊「編輯」才能點選日期進行關閉當日約診提醒'
    },
    hasUnsavedChanges() {
      if (!this.isEdit) return false
      if (this.closedDates.length !== this.ocClosedDates.length) return true

      const currentDates = [...this.closedDates].sort()
      const originalDates = [...this.ocClosedDates].sort()
      return currentDates.some((date, index) => date !== originalDates[index])
    }
  },

  mounted() {
    this.getRemindSettingClose()
  },

  beforeRouteLeave(to, from, next) {
    if (this.hasUnsavedChanges) {
      this.to = to
      this.onLeaveWithoutSave(next)
    } else {
      next()
    }
  },

  methods: {
    setToday() {
      this.focus = ''
    },
    prev() {
      this.$refs.calendar.prev()
    },
    next() {
      this.$refs.calendar.next()
    },
    onLeaveWithoutSave(next) {
      this.confirmTitle = '尚未儲存，確定要離開嗎？'
      this.confirmContent = '請確認內容是否編輯完成'
      this.confirmText = '確定不儲存'
      this.cancelText = '繼續編輯'
      this.confirmColor = 'primary'
      this.cancelColor = 'grey-4'
      this.confirmFunc = (payload) => {
        const { callback } = payload;
        callback && callback()
        next()
      }
      this.toggleConfirmDialog = true
    },
    onClosePast() {
      this.confirmTitle = '操作失敗'
      this.confirmContent = '此日期的通知已送出，無法關閉'
      this.confirmText = '我知道了'
      this.cancelText = ''
      this.confirmColor = 'grey-4'
      this.confirmFunc = (payload) => {
        const { callback } = payload;
        callback && callback()
      }
      this.toggleConfirmDialog = true
    },
    toggleClosed(date) {
      if (!this.isEdit) return

      if (date.past) {
        this.onClosePast()
        return
      }
      if (this.closedDates.includes(date.date)) {
        this.closedDates = this.closedDates.filter(d => d !== date.date)
      } else {
        this.closedDates.push(date.date)
      }
    },
    saveClosed(payload) {
      const { callback } = payload;
      this.loading = true
      setRemindSettingCloseAPI({...this.axiosParamData, dates: this.closedDates}).then(() => {
        this.getRemindSettingClose()
        this.isEdit = false
        callback && callback()
      })
    },
    onConfirm() {
      this.confirmTitle = '請注意！'
      this.confirmContent = '請留意目前的時間，若有看診提醒已發出，是來不及收回的唷！\n可至左側「訊息設定」頁面查看發送時間'
      this.confirmText = '確定關閉提醒'
      this.cancelText = '取消'
      this.confirmColor = 'red-6'
      this.cancelColor = 'grey-7'
      this.confirmFunc = this.saveClosed
      this.toggleConfirmDialog = true
    },
    onCancel() {
      this.closedDates = JSON.parse(JSON.stringify(this.ocClosedDates))
      this.isEdit = false
    },
    onEdit() {
      this.ocClosedDates = JSON.parse(JSON.stringify(this.closedDates))
      this.isEdit = true
    },
    getRemindSettingClose() {
      this.loading = true
      getRemindSettingCloseAPI(this.axiosParamData).then(res => {
        this.closedDates = res.data?.dates || []

        this.loading = false
      })
    }
  }
};
</script>

<style scoped>
.calendar-instruction {
  min-height: 28px;
}

.calendar-day-status {
  min-height: 32px;
}

.calendar-day-hover-icon {
  opacity: 0;
  transition: opacity 0.15s ease-in-out;
}

.calendar--preview {
  cursor: default;
  pointer-events: none;
}

.calendar--preview ::v-deep .v-calendar-weekly__day,
.calendar--preview ::v-deep .v-calendar-weekly__day-label,
.calendar--preview ::v-deep .v-btn {
  cursor: default;
}

.calendar--editable ::v-deep .v-calendar-weekly__day:not(.v-past),
.calendar--editable ::v-deep .v-calendar-weekly__day:not(.v-past) .v-calendar-weekly__day-label,
.calendar--editable ::v-deep .v-calendar-weekly__day:not(.v-past) .v-btn {
  cursor: pointer;
}

.calendar--editable ::v-deep .v-calendar-weekly__day:not(.v-past):hover .calendar-day-hover-icon {
  opacity: 1;
}

.calendar--editable ::v-deep .v-calendar-weekly__day.v-past,
.calendar--editable ::v-deep .v-calendar-weekly__day.v-past .v-calendar-weekly__day-label,
.calendar--editable ::v-deep .v-calendar-weekly__day.v-past .v-btn {
  cursor: default;
}

@media only screen and (max-width: 600px) {
  .close-notification-actions--editing {
    flex-wrap: wrap;
  }

  .close-notification-actions--editing > .spacer {
    display: none;
  }

  .unsaved-message {
    flex-basis: 100%;
    margin: 0 0 8px !important;
    text-align: right;
  }

  .close-notification-actions--editing ::v-deep .v-btn {
    flex: 1 1 0;
    min-width: 0 !important;
    width: auto !important;
  }
}
</style>
